import { Grid, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusBadge } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { OrgUtils } from "@smartrr/shared/entities/Organization";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { RRuleManager } from "@smartrr/shared/entities/Schedule/RRuleManager";
import { toDays } from "@smartrr/shared/utils/renderViewDate";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React from "react";

import { useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import { useToggleModal } from "@customer-app/state/actionCreators/modal";

import { useBasicSubscriptionInfo } from "../../../../Modals/ManageSubscriptionsModal/hooks/useBasicSubscriptionInfo";
import { getFrequencyString } from "../../../../utils";
import { InnerOrderGroup } from "../InnerOrderGroup";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export const ManageSubscriptionCard = ({
  purchaseState,
  subscriptionNumber,
}: {
  purchaseState: IPurchaseState;
  subscriptionNumber: number;
}) => {
  const organization = useOrganization();
  const toggleModal = useToggleModal();
  const billingDays = OrgUtils.getOrgBillingDays(organization);
  const delivery = RRuleManager.fromJson(purchaseState.schedule, OrgUtils.getBillingSchedule(organization!))
    .getFutureActualDeliveries(1, billingDays)
    .map(d => ({ ...d, purchaseState }))
    .find(Boolean)!;

  const { isPaused, containsOOSItems } = useBasicSubscriptionInfo(delivery);

  const { schedule, purchaseStateStatus } = purchaseState;

  // For displaying the order frequency (ie 'Every x Months')
  const freq = getFrequencyString(schedule, organization!);

  const manageSubscriptionModalHandler = () => {
    toggleModal("MANAGE_SUBSCRIPTIONS", {
      deliveryId: getDeliveryId(delivery),
      // TODO: Change over "purchasablesForAdding" to "purchasablesForSubscribing"
      purchasablesForAdding: [],
      generatePurchasables: true,
    });
  };

  return (
    <Grid item md={6} xs={12} key={purchaseState.id}>
      <AquariusCard className={prefixClassName("subscription-card")}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" className={prefixClassName("subscription-name")}>
            {typedLocalize("general.subscriptionNum", {
              num: subscriptionNumber,
            })}
          </Typography>
          {containsOOSItems ? (
            <AquariusBadge
              icon="exclamationMark"
              label={typedLocalize("badgeStatus.importantNotice")}
              sx={{ marginBottom: "7px" }}
            />
          ) : (
            <AquariusBadge status={purchaseStateStatus} sx={{ marginBottom: "7px" }} />
          )}
        </Stack>
        <Typography variant="h3" className={prefixClassName("subscription-delivery-frequency")}>
          {typedLocalize("subscriptions.yourSubscriptions.every", {
            deliveryFrequency: toDays(freq),
          })}
        </Typography>

        {purchaseState ? (
          <InnerOrderGroup
            sx={{ marginTop: "15px" }}
            delivery={delivery!}
            manageSubscriptionModalHandler={manageSubscriptionModalHandler}
          />
        ) : null}
        <AquariusButton filled onClick={() => manageSubscriptionModalHandler()}>
          {localize(
            `subscriptions.yourSubscriptions.${isPaused ? `resumeSubscriptions` : `manageSubscriptions`}`
          )}
        </AquariusButton>
      </AquariusCard>
    </Grid>
  );
};
